/*
 * Copyright (c) 2026. Orion Analytics. All Rights Reserved
 */

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Search, Filter, ArrowUpRight, TrendingUp, ShieldCheck, Layers3, Sparkles } from "lucide-react";

import { fetchMFE } from "../api";

import NAV from "./nav";
import Carousel from "./carousel";
import MFSubnav from "../components/mf/MFSubnav";

const FundExplorerPage = () => {

  const [allFunds, setAllFunds] = useState([]);

  const [selectedCategories, setSelectedCategories] =
    useState([]);

  const [search, setSearch] = useState("");

  const [topCategory, setTopCategory] =
    useState("");

  const [loading, setLoading] = useState(true);

  // =====================================================
  // FETCH
  // =====================================================

  useEffect(() => {

    fetchMFE()

      .then((data) => {

        setAllFunds(data.funds || []);

        setTopCategory(
          data.top_category || ""
        );

        setLoading(false);

      })

      .catch((err) => {

        console.error(err);

        setLoading(false);

      });

  }, []);

  // =====================================================
  // CATEGORY TOGGLE
  // =====================================================

  const toggleCategory = (category) => {

    if (
      selectedCategories.includes(category)
    ) {

      setSelectedCategories(
        selectedCategories.filter(
          (c) => c !== category
        )
      );

    } else {

      setSelectedCategories([
        ...selectedCategories,
        category,
      ]);

    }

  };

  // =====================================================
  // FILTERED FUNDS
  // =====================================================

  const filteredFunds = useMemo(() => {

    return allFunds.filter((fund) => {

      const categoryMatch =
        selectedCategories.length === 0
          ? true
          : selectedCategories.includes(
              fund.category
            );

      const searchMatch =
        fund.scheme_name
          ?.toLowerCase()
          .includes(search.toLowerCase());

      return categoryMatch && searchMatch;

    });

  }, [
    allFunds,
    selectedCategories,
    search,
  ]);

  // =====================================================
  // CATEGORY STATS
  // =====================================================

  const categoryStats = useMemo(() => {

    const stats = {};

    allFunds.forEach((fund) => {

      if (!stats[fund.category]) {

        stats[fund.category] = 0;

      }

      stats[fund.category] += 1;

    });

    return stats;

  }, [allFunds]);

  // =====================================================
  // CATEGORY CARD
  // =====================================================

  const categories = [
    "Equity",
    "Debt",
    "Hybrid",
    "ELSS",
    "Other",
  ];

  // =====================================================
  // RENDER
  // =====================================================

  return (

    // <div className="min-h-screen bg-[#f5f7fb]">
  <div className="bg-gray-50 min-h-screen">

      {/* ========================================= */}
      {/* NAV + HERO */}
      {/* ========================================= */}

      <div className="relative">

        <NAV activetag="mf" />
    <div className="pt-[550px]">
        <MFSubnav active="explorer" />

        <Carousel activetag="mf" />

        {/* DARK OVERLAY */}

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-b
            from-black/50
            via-black/40
            to-black/10
            pointer-events-none
          "
        />

        {/* HERO CONTENT */}

        <div
          className="
            absolute
            inset-0
            flex
            items-center
          "
        >

          <div
            className="
              w-full
              px-6
              lg:px-12
              xl:px-20
            "
          >

            <div className="max-w-4xl">

              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  px-4
                  py-2
                  rounded-full
                  bg-white/10
                  backdrop-blur-md
                  border
                  border-white/20
                  text-white
                  mb-6
                "
              >
                <Sparkles size={18} />
                Institutional Grade Fund Analytics
              </div>

              <h1
                className="
                  text-5xl
                  lg:text-7xl
                  font-black
                  text-white
                  leading-tight
                  mb-6
                "
              >
                Discover The Best
                <span className="block text-green-400">
                  Mutual Funds
                </span>
              </h1>

              <p
                className="
                  text-white/85
                  text-lg
                  lg:text-xl
                  leading-relaxed
                  max-w-2xl
                "
              >
                Explore professionally curated
                mutual funds with advanced
                performance analytics, category
                insights, and institutional-grade
                screening tools.
              </p>

            </div>

          </div>
        </div>
        </div>

      </div>

      {/* ========================================= */}
      {/* MAIN CONTENT */}
      {/* ========================================= */}

      <div
        className="
          relative
          z-10
          -mt-24
          px-4
          lg:px-8
          xl:px-12
          pb-20
        "
      >

        {/* ========================================= */}
        {/* TOP DASHBOARD CARDS */}
        {/* ========================================= */}

        <div
          className="
            grid
            grid-cols-1
            md:grid-cols-2
            xl:grid-cols-4
            gap-6
            mb-8
          "
        >

          {/* CARD 1 */}

          <div
            className="
              bg-white
              rounded-3xl
              p-7
              shadow-sm
              border
              border-gray-100
            "
          >

            <div
              className="
                flex
                items-center
                justify-between
                mb-6
              "
            >

              <div
                className="
                  h-14
                  w-14
                  rounded-2xl
                  bg-green-100
                  flex
                  items-center
                  justify-center
                "
              >
                <Layers3
                  className="text-green-700"
                />
              </div>

              <div
                className="
                  text-sm
                  font-semibold
                  text-green-700
                "
              >
                Live Universe
              </div>

            </div>

            <div
              className="
                text-4xl
                font-black
                text-gray-900
                mb-2
              "
            >
              {allFunds.length}
            </div>

            <div className="text-gray-500">
              Funds Tracked
            </div>

          </div>

          {/* CARD 2 */}

          <div
            className="
              bg-white
              rounded-3xl
              p-7
              shadow-sm
              border
              border-gray-100
            "
          >

            <div
              className="
                flex
                items-center
                justify-between
                mb-6
              "
            >

              <div
                className="
                  h-14
                  w-14
                  rounded-2xl
                  bg-blue-100
                  flex
                  items-center
                  justify-center
                "
              >
                <TrendingUp
                  className="text-blue-700"
                />
              </div>

              <div
                className="
                  text-sm
                  font-semibold
                  text-blue-700
                "
              >
                Top Segment
              </div>

            </div>

            <div
              className="
                text-2xl
                font-black
                text-gray-900
                mb-2
              "
            >
              {topCategory}
            </div>

            <div className="text-gray-500">
              Most Represented Category
            </div>

          </div>

          {/* CARD 3 */}

          <div
            className="
              bg-white
              rounded-3xl
              p-7
              shadow-sm
              border
              border-gray-100
            "
          >

            <div
              className="
                flex
                items-center
                justify-between
                mb-6
              "
            >

              <div
                className="
                  h-14
                  w-14
                  rounded-2xl
                  bg-purple-100
                  flex
                  items-center
                  justify-center
                "
              >
                <ShieldCheck
                  className="text-purple-700"
                />
              </div>

              <div
                className="
                  text-sm
                  font-semibold
                  text-purple-700
                "
              >
                Smart Filters
              </div>

            </div>

            <div
              className="
                text-4xl
                font-black
                text-gray-900
                mb-2
              "
            >
              5
            </div>

            <div className="text-gray-500">
              Fund Categories
            </div>

          </div>

          {/* CARD 4 */}

          <div
            className="
              bg-gradient-to-br
              from-green-600
              to-emerald-500
              rounded-3xl
              p-7
              text-white
              shadow-xl
            "
          >

            <div className="text-white/80 mb-3">
              Investor Insight
            </div>

            <div
              className="
                text-3xl
                font-black
                leading-tight
                mb-5
              "
            >
              Build Wealth
              Smarter
            </div>

            <button
              className="
                bg-white
                text-green-700
                px-5
                py-3
                rounded-2xl
                font-bold
                hover:scale-105
                transition-all
              "
            >
              Start Exploring
            </button>

          </div>

        </div>

        {/* ========================================= */}
        {/* MAIN GRID */}
        {/* ========================================= */}

        <div
          className="
            grid
            grid-cols-1
            xl:grid-cols-[320px_1fr]
            gap-8
          "
        >

          {/* ========================================= */}
          {/* SIDEBAR */}
          {/* ========================================= */}

          <div
            className="
              bg-white
              rounded-3xl
              p-7
              shadow-sm
              border
              border-gray-100
              h-fit
              sticky
              top-[150px]
            "
          >

            {/* SEARCH */}

            <div className="mb-8">

              <div
                className="
                  text-xl
                  font-black
                  text-gray-900
                  mb-5
                "
              >
                Fund Screener
              </div>

              <div className="relative">

                <Search
                  size={18}
                  className="
                    absolute
                    left-4
                    top-1/2
                    -translate-y-1/2
                    text-gray-400
                  "
                />

                <input
                  type="text"
                  placeholder="Search funds..."
                  value={search}
                  align="right"
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  className="
                    w-full
                    h-14
                    rounded-2xl
                    border
                    border-gray-200
                    bg-gray-50
                    pl-12
                    pr-4
                    text-sm
                    outline-none
                    focus:ring-2
                    focus:ring-green-500
                    focus:border-transparent
                  "
                />

              </div>

            </div>

            {/* FILTERS */}

            <div className="mb-8">

              <div
                className="
                  flex
                  items-center
                  gap-2
                  text-lg
                  font-bold
                  mb-5
                "
              >
                <Filter size={18} />
                Categories
              </div>

              <div className="space-y-3">

                {categories.map((category) => {

                  const active =
                    selectedCategories.includes(
                      category
                    );

                  return (

                    <button
                      key={category}
                      onClick={() =>
                        toggleCategory(category)
                      }
                      className={`
                        w-full
                        flex
                        items-center
                        justify-between
                        px-5
                        py-4
                        rounded-2xl
                        border
                        transition-all

                        ${
                          active
                            ? "bg-green-600 text-white border-green-600 shadow-lg"
                            : "bg-gray-50 text-black border-gray-100 hover:border-green-300"
                        }
                      `}
                    >

                      <span className="font-semibold">
                        {category}
                      </span>

                      <span
                        className={`
                          text-sm
                          font-bold

                          ${
                            active
                              ? "text-white"
                              : "text-gray-500"
                          }
                        `}
                      >
                        {categoryStats[
                          category
                        ] || 0}
                      </span>

                    </button>

                  );

                })}

              </div>

            </div>

          </div>

          {/* ========================================= */}
          {/* TABLE */}
          {/* ========================================= */}

          <div>

            {/* HEADER */}

            <div
              className="
                flex
                flex-col
                lg:flex-row
                lg:items-center
                lg:justify-between
                mb-6
                gap-4
              "
            >

              <div>

                <h2
                  className="
                    text-4xl
                    font-black
                    text-gray-900
                    mb-2
                  "
                >
                  Fund Explorer
                </h2>

                <div
                  className="
                    text-gray-500
                    text-lg
                  "
                >
                  {filteredFunds.length} funds
                  available for analysis
                </div>

              </div>

            </div>

            {/* TABLE CARD */}

            <div
              className="
                bg-white
                rounded-3xl
                shadow-sm
                border
                border-gray-100
                overflow-hidden
              "
            >

              {/* TABLE */}

              <div className="overflow-x-auto">

                <table className="w-full">

                  <thead>

                    <tr
                      className="
                        bg-gray-50
                        border-b
                        border-gray-100
                      "
                    >

                      <th
                        className="
                          px-8
                          py-5
                          text-left
                          text-sm
                          font-bold
                          text-gray-500
                        "
                      >
                        Fund
                      </th>

                      <th
                        className="
                          px-8
                          py-5
                          text-left
                          text-sm
                          font-bold
                          text-gray-500
                        "
                      >
                        Category
                      </th>

                      <th
                        className="
                          px-8
                          py-5
                          text-left
                          text-sm
                          font-bold
                          text-gray-500
                        "
                      >
                        1Y Return
                      </th>

                      <th
                        className="
                          px-8
                          py-5
                          text-left
                          text-sm
                          font-bold
                          text-gray-500
                        "
                      >
                        Risk
                      </th>

                      <th
                        className="
                          px-8
                          py-5
                          text-right
                          text-sm
                          font-bold
                          text-gray-500
                        "
                      >
                        Action
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {loading ? (

                      <tr>

                        <td
                          colSpan="5"
                          className="
                            py-16
                            text-center
                            text-gray-500
                          "
                        >
                          Loading funds...
                        </td>

                      </tr>

                    ) : (

                      filteredFunds.map(
                        (fund, index) => (

                          <tr
                            key={fund.isin}
                            className={`
                              border-b
                              border-gray-100
                              hover:bg-green-50/40
                              transition-all

                              ${
                                index % 2 === 0
                                  ? "bg-white"
                                  : "bg-gray-50/30"
                              }
                            `}
                          >

                            {/* FUND */}

                            <td className="px-8 py-6">

                              <div
                                className="
                                  font-bold
                                  text-gray-900
                                  mb-1
                                "
                              >
                                {
                                  fund.scheme_name
                                }
                              </div>

                              <div
                                className="
                                  text-sm
                                  text-gray-500
                                "
                              >
                                {fund.category}
                              </div>

                            </td>

                            {/* CATEGORY */}

                            <td className="px-8 py-6">

                              <div
                                className="
                                  inline-flex
                                  px-4
                                  py-2
                                  rounded-full
                                  bg-gray-100
                                  text-gray-700
                                  text-sm
                                  font-semibold
                                "
                              >
                                {
                                  fund.sub_category
                                }
                              </div>

                            </td>

                            {/* RETURN */}

                            <td className="px-8 py-6">

                              <div
                                className="
                                  text-green-700
                                  font-black
                                  text-lg
                                "
                              >
                                {fund.return_1y
                                  ? `${fund.return_1y}%`
                                  : "--"}
                              </div>

                            </td>

                            {/* RISK */}

                            <td className="px-8 py-6">

                              <div
                                className={`
                                  inline-flex
                                  px-4
                                  py-2
                                  rounded-full
                                  text-sm
                                  font-bold

                                  ${
                                    fund.risk_grade ===
                                    "Low"
                                      ? "bg-green-100 text-green-700"
                                      : fund.risk_grade ===
                                        "Moderate"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-red-100 text-red-700"
                                  }
                                `}
                              >
                                {fund.risk_grade}
                              </div>

                            </td>

                            {/* ACTION */}

                            <td
                              className="
                                px-8
                                py-6
                                text-right
                              "
                            >

                              <a
                                href={`/mfd/${fund.isin}`}
                              >

                                <button
                                  className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    px-5
                                    py-3
                                    rounded-2xl
                                    bg-green-600
                                    hover:bg-green-700
                                    text-white
                                    font-bold
                                    transition-all
                                    hover:scale-105
                                  "
                                >

                                  View

                                  <ArrowUpRight
                                    size={16}
                                  />

                                </button>

                              </a>

                            </td>

                          </tr>

                        )
                      )

                    )}

                  </tbody>

                </table>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );

};

export default FundExplorerPage;